import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { playDing } from "@/components/pos/OnlineOrderNotifier";

// 🔔 Boss bell: a branch manager submitted a stock count (盘点/库存调整) that needs approval.
// Polls /inventory/stocktakes?status=pending every 15s, rings on new ones, re-rings every 2 min while pending.
const TEXTS = {
  zh: { title: "📦 店长提交了库存调整，等你确认", by: "提交人", items: "项", go: "✅ 去确认", hide: "收起", open: "个库存调整待确认", again: "⏰ 库存调整还没确认" },
  es: { title: "📦 Ajuste de stock del encargado pendiente de aprobar", by: "Enviado por", items: "ítems", go: "✅ Revisar y aprobar", hide: "Ocultar", open: "ajuste(s) de stock por aprobar", again: "⏰ Ajuste de stock sin aprobar" },
  en: { title: "📦 Store manager submitted a stock adjustment", by: "By", items: "items", go: "✅ Review & approve", hide: "Hide", open: "stock adjustment(s) to approve", again: "⏰ Stock adjustment still pending" },
  pt: { title: "📦 Ajuste de estoque do gerente aguardando aprovação", by: "Por", items: "itens", go: "✅ Revisar e aprovar", hide: "Ocultar", open: "ajuste(s) por aprovar", again: "⏰ Ajuste de estoque pendente" },
};
const cfg = () => TEXTS[localStorage.getItem("app_lang") || "zh"] || TEXTS.zh;

export default function StocktakeApprovalNotifier() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const knownRef = useRef(new Set());
  const seededRef = useRef(false);
  const lastRingRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/inventory/stocktakes`, { params: { status: "pending" } });
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setItems(list);
        const t = cfg();
        const now = Date.now();
        const fresh = list.filter((a) => !knownRef.current.has(a.id));
        fresh.forEach((a) => knownRef.current.add(a.id));
        if (fresh.length > 0 && seededRef.current) {
          setCollapsed(false);
          playDing("loud");
          lastRingRef.current = now;
          fresh.forEach((a) => toast.warning(`${t.title}: ${a.submitted_by_name || "-"} · ${a.item_count ?? (a.items || []).length} ${t.items}`, { duration: 20000 }));
        } else if (fresh.length > 0 && list.length > 0) {
          // first load with pending ones: one soft ding so the boss notices without being startled
          playDing("crisp");
          lastRingRef.current = now;
        } else if (list.length > 0 && now - lastRingRef.current >= 120000) {
          playDing("crisp");
          lastRingRef.current = now;
          toast.warning(`${t.again} (${list.length})`, { duration: 8000 });
        }
        seededRef.current = true;
      } catch { /* offline — retry next tick */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (items.length === 0) return null;
  const t = cfg();
  if (collapsed) {
    return (
      <button onClick={() => setCollapsed(false)} data-testid="stocktake-notify-collapsed"
        className="fixed bottom-36 right-4 z-[900] bg-amber-500 text-black font-black px-4 py-3 rounded-full shadow-2xl animate-pulse">
        🔔 {items.length} {t.open}
      </button>
    );
  }
  return (
    <div className="fixed bottom-36 right-4 z-[900] w-[min(92vw,380px)] bg-amber-600 border-4 border-amber-200 rounded-2xl shadow-2xl text-white overflow-hidden" data-testid="stocktake-notify-panel">
      <div className="flex items-center justify-between px-4 py-2 bg-amber-700">
        <span className="font-black text-sm">{t.title}</span>
        <button onClick={() => setCollapsed(true)} className="text-xs underline opacity-80" data-testid="stocktake-notify-hide">{t.hide}</button>
      </div>
      <div className="max-h-[40vh] overflow-y-auto divide-y divide-amber-400/60">
        {items.map((a) => (
          <div key={a.id} className="px-4 py-2.5 flex items-center justify-between gap-2" data-testid={`stocktake-notify-${a.id}`}>
            <div className="text-sm">
              <div className="font-black">{a.submitted_by_name || "-"} · {a.item_count ?? (a.items || []).length} {t.items}</div>
              <div className="text-xs opacity-80">{(a.created_at || "").slice(5, 16).replace("T", " ")}</div>
            </div>
            <button onClick={() => { setCollapsed(true); navigate("/admin/warehouses#stocktakes"); }}
              className="bg-white text-amber-700 font-black px-3 py-2 rounded-lg hover:bg-amber-50 whitespace-nowrap" data-testid={`stocktake-notify-go-${a.id}`}>{t.go}</button>
          </div>
        ))}
      </div>
    </div>
  );
}
